<template>
    <div class="flex-col h-full">
        <section class="flex justify-between items-center h-10 mb-4 | lg:h-12 | xl:mb-3 | 2xl:mb-4">
            <div class="flex items-center flex-grow">
                <nio-icon class="mr-4 text-xl text-gray-600 | md:text-2xl" name="regular/search" />
                <input
                    class="w-full bg-transparent"
                    :placeholder="$t('global.general.search')"
                    type="text"
                    v-model="search"
                >
            </div>

            <nio-button
                class="h-full"
                :color="showFilters ? 'blue' : 'white'"
                @click="toggleFilters"
                v-tooltip="$t('chat.conversationList.filter')"
            >
                <nio-icon class="text-lg" name="regular/filter-1" />
            </nio-button>
        </section>

        <slide-up-down
            :active="showFilters"
            :class="{ 'mb-4': showFilters }"
        >
            <conversation-list-filters
                :search="search"
                @toggle-intelligent-filter="toggleIntelligentFilter"
                @toggle-show-active="toggleShowActive"
            />
        </slide-up-down>

        <section class="relative flex flex-col flex-1 rounded-md shadow-xl bg-white overflow-hidden">
            <div class="flex-1 overflow-y-auto">
                <conversation-list-item
                    :conversation="conversation"
                    :data-id="conversation.id"
                    :key="conversation.id"
                    ref="conversationListItems"
                    v-for="conversation in filteredConversations"
                />
                <infinite-loading
                    class="text-center"
                    :class="{ 'h-full flex items-center justify-center': !filteredConversations.length }"
                    :identifier="conversationsFetchId"
                    @infinite="onInfinite"
                >
                    <template #spinner>
                        <div class="flex justify-center p-4">
                            <nio-spinner :size="50" />
                        </div>
                    </template>
                    <template #no-results>
                        <div class="p-4 text-center text-gray-600">
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>

                            {{ $t('chat.conversationList.noResults') }}
                        </div>
                    </template>
                    <template #no-more>
                        <span />
                    </template>
                </infinite-loading>
            </div>

            <div
                class="flex justify-center items-center w-full p-2 border-t border-gray-200 rounded-b-md bg-gray-100"
                v-tooltip="missingValidProviderPhoneTooltip()"
                v-if="!$configs.inIframe"
            >
                <nio-button
                    class="w-full"
                    color="blue"
                    :disabled="!userValidProviderPhones"
                    icon="regular/add"
                    @click="$modal.open('conversation')"
                >
                    {{ $t('chat.conversationList.newConversation') }}
                </nio-button>
            </div>
        </section>

        <conversation-modal />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import orderBy from 'lodash/orderBy.js';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import InfiniteLoading from 'vue-infinite-loading';
    import { formatFullName } from '@/utils/index.js';
    import ConversationListItem from '@/views/Chat/ConversationListItem.vue';
    import ConversationListFilters from '@/components/Chat/ConversationListFilters.vue';
    import ConversationModal from '@/components/Modals/ConversationModal.vue';
    import SlideUpDown from '@/components/Utils/SlideUpDown.js';

    export default {
        components: {
            ConversationListItem,
            ConversationListFilters,
            ConversationModal,
            InfiniteLoading,
            SlideUpDown,
        },

        data() {
            return {
                search: '',
                showFilters: false,
                providerPhones: [],
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('chat', [
                'conversations',
                'conversationsFetchId',
                'conversationsTotal',
                'selectedContactId',
                'selectedConversationId',
                'selectedUserId',
                'showActiveConversation',
                'userRelatedId',
            ]),

            filteredConversations() {
                return this.conversations
                    .filter(conversation => {
                        const user = conversation.users.filter((tmpUser) => {
                            if (!empty(this.userRelatedId) &&
                                conversation.contact_phone.contact_id == this.selectedContactId
                            ) {
                                return tmpUser.related_id == this.userRelatedId;
                            }

                            return tmpUser.id == this.authUser.id || tmpUser.id == this.selectedUserId || !empty(this.search) && tmpUser.account.id == this.authUser.account.id;
                        });

                        return !empty(user);
                    })
                    .filter(conversation => {
                        return conversation.active == this.showActiveConversation || !!this.search;
                    })
                    .sort((a, b) => {
                        if (this.selectedContactId) {
                            if (a.contact_phone.contact_id == this.selectedContactId) {
                                return -1;
                            }

                            if (b.contact_phone.contact_id == this.selectedContactId) {
                                return 1;
                            }
                        }

                        // Sort by latest message updated_at
                        if (a.messages.length && b.messages.length) {
                            const messagesA = orderBy(a.messages, 'created_at', 'desc');
                            const messagesB = orderBy(b.messages, 'created_at', 'desc');

                            const timeA = moment(messagesA[0].created_at);
                            const timeB = moment(messagesB[0].created_at);

                            return timeB.diff(timeA, 'seconds'); // DESC
                        }

                        if (!a.messages.length && b.messages.length) {
                            return 1;
                        }

                        if (!b.messages.length && a.messages.length) {
                            return -1;
                        }

                        // Sort by name
                        const aName = formatFullName(a.contact_phone.contact);
                        const bName = formatFullName(b.contact_phone.contact);

                        if (aName < bName) { return -1 }
                        if (aName > bName) { return 1 }

                        return 0;
                    });
            },

            canFetch() {
                return !!this.selectedUserId && !!this.authUser.account.id;
            },

            userValidProviderPhones() {
                return this.authUser.account.userAvailableProviderPhones(this.authUser.id).length > 0;
            },
        },

        watch: {
            'authUser.id': {
                immediate: true,
                handler(newValue) {
                    this.setSelectedUserId(newValue);
                },
            },

            selectedUserId(newValue) {
                if (newValue) {
                    this.resetFetch();
                }
            },

            search() {
                this.debounceResetFetch();
            },

            filteredConversations() {
                this.scrollIntoView();
            },

            selectedConversationId() {
                this.scrollIntoView();
            },
        },

        methods: {
            ...mapActions('chat', [
                'fetchConversations',
                'setIntelligentFilterEnabled',
            ]),

            ...mapMutations('chat', [
                'setSelectedContactId',
                'setSelectedUserId',
                'setShowActiveConversation',
                'resetFetch',
            ]),

            debounceResetFetch() {
                this.resetFetch();
            },

            async onInfinite($state) {
                if (this.conversationsTotal && this.conversations.length >= this.conversationsTotal) {
                    $state.loaded();
                    $state.complete();
                    return;
                }

                if (!this.canFetch) {
                    $state.complete();
                    return;
                }

                const conversations = await this.fetchConversations({
                    search: this.search,
                });

                if (conversations.length) {
                    $state.loaded();
                } else {
                    $state.complete();
                }
            },

            toggleShowActive(showActive) {
                this.setShowActiveConversation(showActive);
                this.resetFetch();
            },

            toggleIntelligentFilter(isEnabled) {
                this.setIntelligentFilterEnabled(isEnabled);
                this.resetFetch();
            },

            toggleFilters() {
                this.showFilters = !this.showFilters;
            },

            async onConversationCreated(conversation) {
                // Show conversation on top & make sure it is fetched.
                this.setSelectedContactId(conversation.contact_phone.contact_id);
                this.setSelectedUserId(this.authUser.id);
                this.resetFetch();

                this.$router.push({
                    name: 'chat',
                    params: {
                        id: conversation.id,
                    },
                });
            },

            async scrollIntoView() {
                await this.$nextTick();

                if (!this.$refs.conversationListItems || !this.selectedConversationId) {
                    return;
                }

                this.$refs.conversationListItems.forEach(conversationListItem => {
                    if (conversationListItem.$el.dataset.id == this.selectedConversationId) {
                        conversationListItem.$el.scrollIntoView({ block: 'nearest' });
                    }
                });
            },

            missingValidProviderPhoneTooltip() {
                return this.userValidProviderPhones ? '' : this.$t('global.disabled.missingValidProviderPhoneTooltip');
            },
        },

        created() {
            this.debounceResetFetch = debounce(this.debounceResetFetch, 300);
            this.$bus.on('conversation.created', this.onConversationCreated);
        },

        beforeDestroy() {
            this.debounceResetFetch.cancel();
            this.$bus.off('conversation.created', this.onConversationCreated);
            this.setSelectedContactId(null);
        },
    };
</script>
